import { useState,useEffect } from "react";
import CareGoalEditor from "./CareGoalEditor";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Button,
  Divider,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EditIcon from "@mui/icons-material/Edit";

export default function CareGoalCard({ caregoalId, name, description, treatments, followUps }) {
  const groupTreatments = (list) => {
    const grouped = {};
    list.forEach((t) => {
      const type = t.treatmentType?.trim() || "Uncategorized";
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(t);
    });
    return Object.entries(grouped);
  };

  const [localGoal, setLocalGoal] = useState({
    name: "",
    description: "",
    treatments: [],
    followUps: [],
  });

  useEffect(() => {
    setLocalGoal({
      name,
      description,
      treatments,
      followUps,
    });
  }, [name, description, treatments, followUps]);

  const [editorOpen, setEditorOpen] = useState(false);

  const handleEdit = () => {
    setEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setEditorOpen(false);
  };

  const groupedTreatments = groupTreatments(localGoal.treatments);

  return (
    <Card sx={{ borderRadius: 3, boxShadow: 4, overflow: "hidden", mb: 3 }}>
      {/* Header */}
      <CardHeader
        title={`🎯 ${localGoal.name}`}
        titleTypographyProps={{
          variant: "h6",
          sx: { fontWeight: "bold", color: "#0d47a1" },
        }}
        action={
          <IconButton>
            <HelpOutlineIcon />
          </IconButton>
        }
        sx={{ backgroundColor: "#e3f2fd", borderBottom: "1px solid #ccc", px: 3, py: 2 }}
      />

      {/* Content */}
      <CardContent sx={{ px: 3 }}>
        {/* Description */}
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
          Description
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
        {localGoal.description}
        </Typography>

        {/* Treatments */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#1a237e", mb: 1 }}>
          Treatments
        </Typography>

        {groupedTreatments.length === 0 ? (
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            No treatments assigned.
          </Typography>
        ) : (
          groupedTreatments.map(([type, group], idx) => (
            <Box key={idx} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: "bold", color: "#0d47a1", mb: 1 }}>
                {type}
              </Typography>
              {group.map((treat) => (
                <Box
                  key={treat.patientTreatmentId}
                  sx={{
                    backgroundColor: "#f5f5f5",
                    borderRadius: 2,
                    p: 1.5,
                    mb: 1,
                    pl: 2,
                    borderLeft: "4px solid #1976d2",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {treat.treatmentName?.trim()}
                  </Typography>
                  <Typography variant="body2">
                    {treat.treatmentDescription?.trim()}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))
        )}

        {/* Follow Ups */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#1a237e", mb: 1 }}>
          Follow Ups
        </Typography>

        {localGoal.followUps.length === 0 ? (
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            No follow up records.
          </Typography>
        ) : (
          <Box>
            {localGoal.followUps.map((fup) => (
              <Box
                key={fup.patientFollowUpId}
                sx={{
                  backgroundColor: "#fff3e0",
                  borderRadius: 2,
                  p: 1.5,
                  mb: 1,
                  pl: 2,
                  borderLeft: "4px solid #fb8c00",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {fup.followUpName?.trim()}
                </Typography>
                <Typography variant="body2">
                  {fup.followUpDescription?.trim()}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>

      {/* Actions */}
      <CardActions sx={{ justifyContent: "flex-end", px: 3, pb: 2 }}>
      <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={handleEdit}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: "bold",
            color: "#0d47a1",
            borderColor: "#0d47a1",
            "&:hover": {
              backgroundColor: "#e3f2fd",
              borderColor: "#0d47a1",
            },
          }}
        >
          Edit
        </Button>
      </CardActions>
      <CareGoalEditor
        open={editorOpen}
        onClose={handleCloseEditor}
        goalData={{
          caregoalId,
          name: localGoal.name,
          description: localGoal.description,
          treatments: localGoal.treatments,
          followUps: localGoal.followUps,
        }}
        onSave={(updatedGoal) => {
          setLocalGoal(updatedGoal);
          setEditorOpen(false);
        }}
      />

    </Card>
  );
}
